import { getRestApiUrl } from '../config';
import { userRegionVar } from '../graphql/reactiveVariables';
import { EmailAddressUnknown, GeneratedPassword, ConflictingEmail, MissingEmail } from './errors';

export enum Provider {
    Facebook = 'facebook',
    Apple = 'apple',
    Google = 'google',
}

export async function getUserId(email: string): Promise<string> {
    console.log(`API call: getting the user id for email ${email}...`);
    const headers: Headers = new Headers();
    headers.append('Accept', 'application/json');
    headers.append('Content-Type', 'application/json');
    const url = `${getRestApiUrl()}user/id?email=${encodeURIComponent(email)}`;
    return fetch(url, {
        method: 'GET',
        headers: headers,
    })
        .then((response) => {
            console.log(`Response ${response.status} for API call 'get user id'`);
            if (response.status === 200) {
                return response.json();
            } else if (response.status === 422) {
                throw new EmailAddressUnknown();
            } else if (response.status === 409) {
                throw new GeneratedPassword();
            } else {
                throw new Error(`${response.status}`);
            }
        })
        .then((responseJSON) => responseJSON.userId);
}

export interface GetSocialIdentityCredentialsParams {
    provider: string;
    providerUserId: string;
    email?: string;
    secretToken?: string;
}

export interface GetSocialIdentityCredentialsResponse {
    userId: string;
    secretKey: string;
    new: boolean;
}

export async function getSocialIdentityCredentials(
    params: GetSocialIdentityCredentialsParams
): Promise<GetSocialIdentityCredentialsResponse> {
    const { provider, providerUserId, email, secretToken } = params;
    console.log(`API call: getting social identity credentials for user ${providerUserId} for provider ${provider}...`);
    const headers: Headers = new Headers();
    headers.append('Accept', 'application/json');
    headers.append('Content-Type', 'application/json');
    const url =
        `${getRestApiUrl()}user/social-identity` +
        `?provider=${encodeURIComponent(provider)}` +
        `&providerUserId=${encodeURIComponent(providerUserId)}` +
        `${email ? `&email=${encodeURIComponent(email)}` : ''}` +
        `${secretToken ? `&secretToken=${encodeURIComponent(secretToken)}` : ''}`;
    return fetch(url, {
        method: 'GET',
        headers: headers,
    }).then((response) => {
        console.log(`Response ${response.status} for API call 'get social identity'`);
        if (response.status === 200) return response.json();
        else if (response.status === 409) throw new ConflictingEmail(email);
        else if (response.status === 422) throw new MissingEmail();
        else throw new Error(`${response.status}`);
    });
}

/**
 * Enum representing possible referrer code validation errors.
 */
export enum UserReferrerValidationErrorCode {
    /** The referrer is in a different region than the referee */
    DifferentRegion = 'different_region',
    /** The referrer has reached their maximum number of referrals */
    RefereeLimitReached = 'referee_limit_reached',
    /** The referrer's referral program is not yet activated */
    ReferralNotActivated = 'referral_not_activated',
    /** No user found with the given referrer code */
    NoMatchingReferrerUser = 'no_matching_referrer_user',
    /** The IP address has exceeded the event rate limit */
    IpAddressEventRateLimitExceeded = 'ip_address_event_rate_limit_exceeded',
    /** An unknown error occurred (e.g., network failure) */
    Unknown = 'unknown',
}

export interface CheckUserReferrerCodeResponse {
    referrerCode: string;
    isReferrerCodeValid: boolean;
    referrerFirstName?: string;
    specialReferralCodeName?: string;
    error?: UserReferrerValidationErrorCode;
}

/**
 * Validates a referrer code by calling the backend API.
 *
 * This function checks whether a referrer code is valid and eligible for use.
 * The backend performs the following validations:
 * - Verifies the referrer exists
 * - Ensures the referrer and referee are in the same region (cross-region referrals are blocked)
 * - Checks the referrer hasn't exceeded their referral limit (ambassador program cap)
 * - Verifies the referrer's referral activation conditions are met
 *
 * @param referrerCode - The referral code to validate (should be lowercase, 6+ characters)
 * @returns Promise resolving to validation result including:
 *   - `isReferrerCodeValid`: Whether the code can be used
 *   - `referrerFirstName`: The referrer's name (if valid)
 *   - `specialReferralCodeName`: Name for special/promotional codes
 *   - `error`: Error code if invalid ('different_region', 'referee_limit_reached', 'referral_not_activated', 'no_matching_referrer_user')
 */
export async function checkUserReferrerCode(referrerCode: string): Promise<CheckUserReferrerCodeResponse> {
    const region = userRegionVar();
    console.log(`API call: checking the user referrer code ${referrerCode}...`);
    const headers: Headers = new Headers();
    headers.append('Accept', 'application/json');
    headers.append('Content-Type', 'application/json');
    let url = `${getRestApiUrl()}user/referrer-code/check?referrerCode=${encodeURIComponent(referrerCode)}`;
    if (region) url += `&region=${region}`;
    return fetch(url, { method: 'GET', headers }).then((response) => {
        console.log(`Response ${response.status} for API call 'check user referrer code'`);
        if (response.status === 200) return response.json();
        else throw new Error(`${response.status}`);
    });
}
